import React, {
    FC,
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import useIsMountedRef from 'use-is-mounted-ref'
import Button from '@material-ui/core/Button'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, PromptContext, ReactState } from '../../../../React/Common/Contexts'
import { PromptDisplayType } from '../../../../React/Common/Contexts/PromptContext'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn, DataTableSelectionBridge } from '../../Molecules/DataTable/DataTable'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { GenericAdminFilterLanguage, Router as RouterLang, LogManagementListLanguage } from '../../../../../i18n'
import { UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import {
    getLogManagementStatusList,
    postLogManagementReset,
    postLogManagementStart,
    postLogManagementStop
} from '../../../../React/Common/API/ItemsGetters'
import {
    LogManagementListItem,
    LogManagementListResponse,
    LogManagementListRow
} from '../../../../NonReact/Types/LogManagementListTypes'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { logManagementDownloadURL } from '../../../../NonReact/constants/ApiConstants'
import type {
    LogManagementScopeQueryParams,
    LogManagementStatusQueryParams
} from '../../../../NonReact/constants/ApiConstants'
import { parseAdminListUrlSearchParams, type AdminListUrlBootstrap } from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialAdminScopeFilters } from '../../../Utils/adminListFilterReadiness'

const isConcreteTelSysFilterValue = (value: unknown): boolean =>
    isVarNotEmpty(value) &&
    value !== ALL_VALUE &&
    value !== ANY_VALUE_STRING_NUMERIC &&
    value !== EMPTY_STRING

/**
 * Routed log file management list (in-memory {@code CLogging} status and control).
 *
 * @returns Log management page content.
 */
export const LogManagementList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(LogManagementListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const [rows, setRows]: ReactState<LogManagementListRow[]> = useState<LogManagementListRow[]>([])
    const [selectedRows, setSelectedRows]: ReactState<LogManagementListRow[]> = useState<LogManagementListRow[]>([])
    const [canMutateLogging, setCanMutateLogging]: ReactState<boolean> = useState<boolean>(false)
    const [hasLogFilesDownloadPermission, setHasLogFilesDownloadPermission]: ReactState<boolean> =
        useState<boolean>(false)

    const {
        loading,
        setLoading,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState(25)

    const [userInfo]: UserInfo = useUserInfo()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const selectionBridgeRef: MutableRefObject<OnlyNullable<DataTableSelectionBridge>> =
        useRef<OnlyNullable<DataTableSelectionBridge>>(null)

    const [searchParams] = useSearchParams()
    const urlBootstrap: OnlyNullable<AdminListUrlBootstrap> = useMemo((): OnlyNullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])

    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)
    const [hasCompletedInitialListFetch, setHasCompletedInitialListFetch]: ReactState<boolean> = useState<boolean>(false)

    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad

    const listCmeId: Nullable<string> = (filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID) as Nullable<string>
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listSearch: string = filters?.search ?? EMPTY_STRING

    const listTelSysIdParam: string = isConcreteTelSysFilterValue(filters?.telephonyServer?.value)
        ? String(filters?.telephonyServer?.value)
        : String(ALL_VALUE)

    const listScope: OnlyNullable<LogManagementScopeQueryParams> =
        useMemo((): OnlyNullable<LogManagementScopeQueryParams> => {
            if (IsVarEmpty(listCmeId)) {
                return null
            }

            return {
                cmeId: listCmeId,
                recursive: listIncludeRecursive,
                telSysId: listTelSysIdParam
            }
        }, [listCmeId, listIncludeRecursive, listTelSysIdParam])

    useAdminListLegacyServletSwitch(ServletCmds.LOGFILEMGR, filters, userInfo?.Scope?.CMEnvironmentID)

    const getLogList = useCallback((): void => {
        if (!hasReceivedInitialFilters) return

        if (isBootstrappingFromUrl) return

        if (IsVarEmpty(listScope)) {
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasCompletedInitialListFetch(true)

            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const pageStartIndex: number = currentPage * rowsPerPage
        const searchQuery: Definable<string> = listSearch.trim() === EMPTY_STRING ? undefined : listSearch.trim()

        const query: LogManagementStatusQueryParams = {
            ...listScope,
            offset: pageStartIndex,
            limit: rowsPerPage,
            ...(isVarNotEmpty(searchQuery) ? { searchQuery } : {})
        }

        getLogManagementStatusList(query)
            .then((response: Nullable<LogManagementListResponse>): void => {
                if (!isMounted.current) return

                const items: readonly LogManagementListItem[] = response?.Data?.Items ?? []
                const mapped: LogManagementListRow[] = items.map(
                    (item: LogManagementListItem, index: number): LogManagementListRow => {
                        const fileName: string = item.FileName ?? EMPTY_STRING

                        return {
                            key: `${fileName}-${String(pageStartIndex)}-${String(index)}`,
                            Description: item.Description ?? EMPTY_STRING,
                            ClassName: item.ClassName ?? EMPTY_STRING,
                            FileName: fileName,
                            FileSizeDisplay: item.FileSizeDisplay ?? EMPTY_STRING,
                            IsLoggingActive: Boolean(item.IsLoggingActive)
                        }
                    }
                )

                setRows(mapped)
                setTotalCount(response?.Data?.Total ?? 0)
                setCanMutateLogging(Boolean(response?.Data?.CanMutateLogging))
                setHasLogFilesDownloadPermission(Boolean(response?.Data?.HasLogFilesDownloadPermission))
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)

                if (isMounted.current) {
                    setLoading((currentLoading: number): number => currentLoading - 1)
                    setHasCompletedInitialListFetch(true)
                }
            })
    }, [
        hasReceivedInitialFilters,
        isBootstrappingFromUrl,
        listScope,
        listSearch,
        pagination,
        setLoading,
        setTotalCount
    ])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    useEffect((): void => {
        getLogList()
    }, [getLogList])

    const refreshList = useCallback((): void => {
        getLogList()
    }, [getLogList])

    const isLogManagementTableLoading: boolean =
        loading > 0 ||
        !hasReceivedInitialFilters ||
        isWaitingForInitialUrlBootstrapLoad ||
        (hasReceivedInitialFilters && isStringNotEmpty(listCmeId ?? EMPTY_STRING) && !hasCompletedInitialListFetch)

    const selectedFileNames: readonly string[] = useMemo(
        (): readonly string[] =>
            selectedRows.map((r: LogManagementListRow): string => r.FileName).filter(isStringNotEmpty),
        [selectedRows]
    )

    const selectedStoppedFileNames: readonly string[] = useMemo(
        (): readonly string[] =>
            selectedRows
                .filter((row: LogManagementListRow): boolean => !row.IsLoggingActive)
                .map((row: LogManagementListRow): string => row.FileName)
                .filter(isStringNotEmpty),
        [selectedRows]
    )

    const selectedRunningFileNames: readonly string[] = useMemo(
        (): readonly string[] =>
            selectedRows
                .filter((row: LogManagementListRow): boolean => row.IsLoggingActive)
                .map((row: LogManagementListRow): string => row.FileName)
                .filter(isStringNotEmpty),
        [selectedRows]
    )

    const runMutationThenRefresh = useCallback(
        (promise: Promise<unknown>): void => {
            setLoading((c: number): number => c + 1)
            promise
                .then((): void => {
                    getLogList()
                })
                .catch(displayError)
                .finally((): void => {
                    if (isMounted.current) setLoading((c: number): number => c - 1)
                })
        },
        [getLogList, isMounted, setLoading]
    )

    const handleStart = useCallback((): void => {
        if (IsVarEmpty(listScope) || selectedStoppedFileNames.length === 0) return

        runMutationThenRefresh(postLogManagementStart(listScope, selectedStoppedFileNames))
    }, [listScope, runMutationThenRefresh, selectedStoppedFileNames])

    const handleStop = useCallback((): void => {
        if (IsVarEmpty(listScope) || selectedRunningFileNames.length === 0) return

        const stopBody: string = `${language.get(
            LogManagementListLanguage.LogManagementList_Confirm_StopBody
        )}\n\n${language.get(LogManagementListLanguage.LogManagementList_Confirm_Generic)}`

        displayPromptPromise(stopBody, language.get(LogManagementListLanguage.LogManagementList_Confirm_StopTitle))
            .then((): void => {
                runMutationThenRefresh(postLogManagementStop(listScope, selectedRunningFileNames))
            })
            .catch((): void => {
                // cancelled
            })
    }, [displayPromptPromise, language, listScope, runMutationThenRefresh, selectedRunningFileNames])

    const handleReset = useCallback((): void => {
        if (IsVarEmpty(listScope) || selectedFileNames.length === 0) return

        const resetBody: string = `${language.get(
            LogManagementListLanguage.LogManagementList_Confirm_ResetBody
        )}\n\n${language.get(LogManagementListLanguage.LogManagementList_Confirm_Generic)}`

        displayPromptPromise(resetBody, language.get(LogManagementListLanguage.LogManagementList_Confirm_ResetTitle))
            .then((): void => {
                runMutationThenRefresh(postLogManagementReset(listScope, selectedFileNames))
            })
            .catch((): void => {
                // cancelled
            })
    }, [displayPromptPromise, language, listScope, runMutationThenRefresh, selectedFileNames])

    const handleDownload = useCallback((): void => {
        if (selectedFileNames.length === 0) return

        const url: string = logManagementDownloadURL(selectedFileNames).constructURL()
        window.open(url, '_blank', 'noopener,noreferrer')
    }, [selectedFileNames])

    const columns: DataTableColumn<LogManagementListRow, keyof LogManagementListRow>[] = useMemo(
        (): DataTableColumn<LogManagementListRow, keyof LogManagementListRow>[] => [
            {
                headerName: language.get(LogManagementListLanguage.LogManagementList_Column_Name),
                field: 'Description',
                key: 'log-mgmt-description',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(LogManagementListLanguage.LogManagementList_Column_ClassName),
                field: 'ClassName',
                key: 'log-mgmt-class',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(LogManagementListLanguage.LogManagementList_Column_FileName),
                field: 'FileName',
                key: 'log-mgmt-file',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(LogManagementListLanguage.LogManagementList_Column_FileSize),
                field: 'FileSizeDisplay',
                key: 'log-mgmt-size',
                sortable: false,
                align: 'left'
            },
            {
                headerName: language.get(LogManagementListLanguage.LogManagementList_Column_Status),
                field: 'IsLoggingActive',
                key: 'log-mgmt-status',
                sortable: false,
                align: 'left',
                renderCell: (row: LogManagementListRow): string =>
                    row.IsLoggingActive
                        ? language.get(LogManagementListLanguage.LogManagementList_Status_Running)
                        : language.get(LogManagementListLanguage.LogManagementList_Status_NotRunning)
            }
        ],
        [language]
    )

    const tableToolbarFooter: ReactElement = useMemo((): ReactElement => {
        const showMutateActions: boolean = canMutateLogging && selectedFileNames.length > 0
        const showStart: boolean = showMutateActions && selectedStoppedFileNames.length > 0
        const showStop: boolean = showMutateActions && selectedRunningFileNames.length > 0
        const showReset: boolean = showMutateActions
        const showDownload: boolean = hasLogFilesDownloadPermission && selectedFileNames.length > 0

        return (
            <>
                {showStop ? (
                    <Button color='default' variant='contained' onClick={handleStop} data-testid='log-mgmt-stop'>
                        {language.get(LogManagementListLanguage.LogManagementList_Action_Stop)}
                    </Button>
                ) : null}
                {showStart ? (
                    <Button color='primary' variant='contained' onClick={handleStart} data-testid='log-mgmt-start'>
                        {language.get(LogManagementListLanguage.LogManagementList_Action_Start)}
                    </Button>
                ) : null}
                {showReset ? (
                    <Button color='default' variant='contained' onClick={handleReset} data-testid='log-mgmt-reset'>
                        {language.get(LogManagementListLanguage.LogManagementList_Action_Reset)}
                    </Button>
                ) : null}
                {showDownload ? (
                    <Button color='default' variant='contained' onClick={handleDownload} data-testid='log-mgmt-download'>
                        {language.get(LogManagementListLanguage.LogManagementList_Action_Download)}
                    </Button>
                ) : null}
            </>
        )
    }, [
        language,
        handleStop,
        handleStart,
        handleReset,
        handleDownload,
        canMutateLogging,
        hasLogFilesDownloadPermission,
        selectedFileNames,
        selectedStoppedFileNames,
        selectedRunningFileNames
    ])

    return (
        <div className='log-management-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_logManagement} />
            <div className='log-management-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialAdminScopeFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    onRefresh={refreshList}
                    searchPlaceholder={language.get(LogManagementListLanguage.LogManagementList_SearchPlaceholder)}
                />
                <DataTable<LogManagementListRow, keyof LogManagementListRow>
                    permissionName={AdminBackendPermissionNames.LOG_MGMT}
                    localeText={{
                        noRows: language.get(LogManagementListLanguage.LogManagementList_Empty)
                    }}
                    columns={columns}
                    data={rows}
                    defaultRowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                    defaultRowsPerPage={25}
                    totalCount={totalCount}
                    isLoading={isLogManagementTableLoading}
                    paginationServer
                    isSelectable
                    preserveSelectionOnDataChange
                    toolbarDisabled
                    toolbarFooterSlot={tableToolbarFooter}
                    selectionBridgeRef={selectionBridgeRef}
                    onSelectionChange={(items: LogManagementListRow[]): void => {
                        setSelectedRows(items)
                    }}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
            </div>
        </div>
    )
}
